import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  Activity,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  User,
  Code,
  GitBranch,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Info,
} from 'lucide-react';
import { useActivityStore, useCoverageStore, useDriftStore } from '@/stores/skillsIntelligence';
import { DEFAULT_ORG } from '@/stores/skillRegistry';
import type { ActivityEvent, CoverageRow, DriftCase } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const SCOPE_CFG: Record<string, { label: string; classes: string; dot: string }> = {
  client: {
    label: 'client',
    classes: 'bg-blue-50 text-blue-600 border-blue-100',
    dot: 'bg-blue-500',
  },
  org: {
    label: 'org',
    classes: 'bg-brand-teal/10 text-brand-teal border-brand-teal/20',
    dot: 'bg-brand-teal',
  },
  platform_default: {
    label: 'default',
    classes: 'bg-slate-100 text-slate-500 border-slate-200',
    dot: 'bg-slate-400',
  },
};

const TOOL_COLORS: Record<string, string> = {
  get_creative_brief: 'text-blue-500',
  generate_creative_angles: 'text-amber-500',
  generate_ugc_script: 'text-purple-500',
  generate_video_prompts: 'text-rose-500',
  get_ad_pack: 'text-emerald-500',
};

// ---------------------------------------------------------------------------
// Resolution trace drawer
// ---------------------------------------------------------------------------

function ResolutionTrace({ event, onClose }: { event: ActivityEvent; onClose: () => void }) {
  const steps = [
    {
      label: `Check client scope: ${event.client_uid}`,
      result:
        event.resolved_scope === 'client'
          ? 'Found matching skill'
          : 'No client-scoped skill',
      ok: event.resolved_scope === 'client',
    },
    {
      label: 'Check org-wide scope',
      result:
        event.resolved_scope === 'org'
          ? 'Found matching skill'
          : event.resolved_scope === 'client'
            ? 'Skipped (client scope resolved)'
            : 'No org-wide skill',
      ok: event.resolved_scope === 'org',
      skipped: event.resolved_scope === 'client',
    },
    {
      label: 'Fall through to platform defaults',
      result:
        event.resolved_scope === 'platform_default'
          ? 'Using platform defaults'
          : 'Not reached',
      ok: false,
      skipped: event.resolved_scope !== 'platform_default',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 animate-slide-up">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-navy-900">Resolution Trace</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-navy-900 transition-colors">
            ✕
          </button>
        </div>

        <div className="bg-slate-50 rounded-lg border border-slate-100 px-4 py-3 space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <Code size={13} className={TOOL_COLORS[event.tool_name] ?? 'text-slate-500'} />
            <span className="font-mono text-xs">{event.tool_name}</span>
            <span className="text-slate-400">·</span>
            <span className="text-xs text-slate-500">{event.client_uid}</span>
          </div>
          <p className="text-[10px] text-slate-400 font-mono">{event.event_id}</p>
        </div>

        <div className="space-y-1">
          {steps.map((step, i) => (
            <div
              key={i}
              className={clsx(
                'flex items-start gap-3 px-3 py-2.5 rounded-lg',
                step.skipped
                  ? 'opacity-40'
                  : step.ok
                    ? 'bg-emerald-50 border border-emerald-100'
                    : event.resolved_scope === 'platform_default' && i === 2
                      ? 'bg-amber-50 border border-amber-100'
                      : 'bg-white border border-slate-100',
              )}
            >
              <div className="flex-shrink-0 mt-0.5">
                {step.skipped ? (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-200" />
                ) : step.ok ? (
                  <CheckCircle2 size={16} className="text-emerald-500" />
                ) : event.resolved_scope === 'platform_default' && i === 2 ? (
                  <AlertTriangle size={16} className="text-amber-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-navy-900">{step.label}</p>
                <p className="text-[11px] text-slate-500">{step.result}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-[10px] text-slate-400">Skill</p>
            <p className="text-xs font-mono font-semibold text-navy-900 truncate">{event.skill_id || '—'}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-slate-400">Version</p>
            <p className="text-xs font-mono font-semibold text-navy-900">{event.version || '—'}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-slate-400">Latency</p>
            <p className="text-xs font-semibold text-navy-900">{event.latency_ms}ms</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity feed
// ---------------------------------------------------------------------------

function ActivityFeed({ events, loading }: { events: ActivityEvent[]; loading: boolean }) {
  const [traceEvent, setTraceEvent] = useState<ActivityEvent | null>(null);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading activity…
      </div>
    );
  }

  return (
    <>
      <div className="space-y-1.5">
        {events.map((e) => {
          const scope = SCOPE_CFG[e.resolved_scope] ?? SCOPE_CFG.platform_default;
          return (
            <button
              key={e.event_id}
              onClick={() => setTraceEvent(e)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-slate-100 rounded-xl hover:border-slate-200 hover:shadow-soft transition-all text-left group"
            >
              <div className={clsx('w-2 h-2 rounded-full flex-shrink-0', scope.dot)} />

              <div className="flex-1 min-w-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 items-center">
                <Code size={12} className={clsx('flex-shrink-0', TOOL_COLORS[e.tool_name] ?? 'text-slate-400')} />
                <span className="text-xs font-mono font-medium text-navy-900 truncate">{e.tool_name}</span>

                <User size={11} className="text-slate-400 flex-shrink-0" />
                <span className="text-[11px] text-slate-500 truncate">{e.user || 'Shared connector'}</span>
              </div>

              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span
                  className={clsx(
                    'text-[9px] font-semibold border px-1.5 py-0.5 rounded-full uppercase',
                    scope.classes,
                  )}
                >
                  {scope.label}
                </span>
                <span className="text-[10px] text-slate-400">{timeAgo(e.timestamp)}</span>
              </div>

              <ChevronDown size={12} className="text-slate-300 group-hover:text-brand-teal transition-colors flex-shrink-0" />
            </button>
          );
        })}
      </div>
      {traceEvent && <ResolutionTrace event={traceEvent} onClose={() => setTraceEvent(null)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Coverage heatmap
// ---------------------------------------------------------------------------

function CoverageHeatmap({
  rows,
  tools,
  platformDefaults,
  loading,
}: {
  rows: CoverageRow[];
  tools: string[];
  platformDefaults: Record<string, number>;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading coverage…
      </div>
    );
  }

  const maxVal = Math.max(1, ...rows.flatMap((r) => Object.values(r.cells)));

  function cellColor(val: number): string {
    if (val === 0) return 'bg-slate-50 text-slate-300';
    const intensity = val / maxVal;
    if (intensity >= 0.7) return 'bg-brand-teal/80 text-white';
    if (intensity >= 0.4) return 'bg-brand-teal/40 text-brand-teal';
    return 'bg-brand-teal/15 text-brand-teal';
  }

  const shortTool = (t: string) =>
    t.replace('get_', '').replace('generate_', '').replace(/_/g, '\u00A0').substring(0, 12);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left text-[10px] text-slate-400 font-mono pr-4 pb-2 whitespace-nowrap">Skill</th>
            {tools.map((t) => (
              <th key={t} className="text-[10px] text-slate-400 font-mono pb-2 px-1 text-center whitespace-nowrap">
                {shortTool(t)}
              </th>
            ))}
            <th className="text-[10px] text-slate-400 font-mono pb-2 px-1 text-center">Total</th>
          </tr>
        </thead>
        <tbody className="space-y-1">
          {rows.map((row) => (
            <tr key={row.skill_id} className="group">
              <td className="pr-4 py-1 font-mono text-[11px] text-navy-700 whitespace-nowrap group-hover:text-navy-900 transition-colors">
                {row.skill_id}
              </td>
              {tools.map((t) => {
                const val = row.cells[t] ?? 0;
                return (
                  <td key={t} className="px-1 py-1 text-center">
                    <span
                      className={clsx(
                        'inline-flex items-center justify-center w-8 h-6 rounded text-[10px] font-semibold transition-all',
                        cellColor(val),
                      )}
                    >
                      {val || '—'}
                    </span>
                  </td>
                );
              })}
              <td className="px-1 py-1 text-center">
                <span className="text-[11px] font-semibold text-navy-900">{row.total}</span>
              </td>
            </tr>
          ))}

          {/* Platform defaults row */}
          <tr className="border-t border-slate-100 border-dashed">
            <td className="pr-4 py-1 text-[11px] text-slate-400 font-mono italic">platform defaults</td>
            {tools.map((t) => {
              const val = platformDefaults[t] ?? 0;
              return (
                <td key={t} className="px-1 py-1 text-center">
                  {val > 0 ? (
                    <span className="inline-flex items-center justify-center w-8 h-6 rounded text-[10px] font-semibold bg-amber-50 text-amber-500">
                      {val}
                    </span>
                  ) : (
                    <span className="text-slate-200 text-[10px]">—</span>
                  )}
                </td>
              );
            })}
            <td className="px-1 py-1 text-center text-[11px] text-amber-500 font-semibold">
              {Object.values(platformDefaults).reduce((a, b) => a + b, 0)}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-400">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-brand-teal/80 inline-block" />
          High volume
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-brand-teal/15 inline-block" />
          Low volume
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-amber-50 border border-amber-100 inline-block" />
          Platform default (no binding)
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version drift table
// ---------------------------------------------------------------------------

function DriftTable({ cases, loading }: { cases: DriftCase[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading drift…
      </div>
    );
  }
  if (!cases.length) {
    return (
      <div className="flex items-center gap-2 text-emerald-600 text-sm bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
        <CheckCircle2 size={16} />
        All team members are on the latest published skill versions.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {cases.map((c, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 bg-white border border-slate-100 rounded-xl"
        >
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-navy-900">{c.user}</span>
              <span className="text-[10px] text-slate-400">on</span>
              <span className="text-[11px] font-mono text-slate-600">{c.skill_id}</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">{c.client_uid}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 text-right">
            <div>
              <div className="flex items-center gap-1 justify-end">
                <span className="text-[10px] font-mono bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                  {c.resolved_version}
                </span>
                <span className="text-[10px] text-slate-400">→</span>
                <span className="text-[10px] font-mono bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                  {c.latest_version}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">{c.behind_by} behind</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type TabId = 'feed' | 'heatmap' | 'drift';

export function UsageActivityPage() {
  const { events, total, loading: feedLoading, fetch: fetchActivity } = useActivityStore();
  const { rows, tools, platformDefaults, loading: coverageLoading, fetch: fetchCoverage } = useCoverageStore();
  const { cases, totalDrift, loading: driftLoading, fetch: fetchDrift } = useDriftStore();
  const [tab, setTab] = useState<TabId>('feed');

  const fetchAll = () => {
    fetchActivity(DEFAULT_ORG, 40);
    fetchCoverage(DEFAULT_ORG);
    fetchDrift(DEFAULT_ORG);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const defaultCalls = Object.values(platformDefaults).reduce((a, b) => a + b, 0);
  const boundCalls = total - defaultCalls;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Activity size={22} className="text-blue-500" />
            Usage & Activity
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Real-time MCP skill call telemetry — what's firing, who's using it, and where customization is taking effect.
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy-900 transition-colors"
        >
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip label="Total calls" value={total} color="text-navy-900" />
        <StatChip label="Bound calls" value={boundCalls} color="text-brand-teal" />
        <StatChip label="Platform defaults" value={defaultCalls} color="text-amber-600" />
        <StatChip label="Version drift" value={totalDrift} color={totalDrift > 0 ? 'text-red-500' : 'text-emerald-600'} />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100">
        {(
          [
            { id: 'feed', label: 'Activity Feed' },
            { id: 'heatmap', label: 'Coverage Heatmap' },
            { id: 'drift', label: `Version Drift${totalDrift > 0 ? ` (${totalDrift})` : ''}` },
          ] as { id: TabId; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-navy-900',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'feed' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-blue-400 flex-shrink-0" />
              Click any event row to see the full SkillResolver priority trace for that call.
            </div>
            <ActivityFeed events={events} loading={feedLoading} />
          </>
        )}
        {tab === 'heatmap' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-blue-400 flex-shrink-0" />
              Each cell shows call volume where a binding resolved. Amber "platform defaults" row shows unbound calls.
            </div>
            <div className="bg-white border border-slate-100 rounded-xl p-4">
              <CoverageHeatmap rows={rows} tools={tools} platformDefaults={platformDefaults} loading={coverageLoading} />
            </div>
          </>
        )}
        {tab === 'drift' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-blue-400 flex-shrink-0" />
              Team members resolving older skill versions than the current latest published.
            </div>
            <DriftTable cases={cases} loading={driftLoading} />
          </>
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl px-4 py-3">
      <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">{label}</p>
      <p className={clsx('text-xl font-bold mt-0.5', color)}>{value}</p>
    </div>
  );
}
